import React, { useState } from "react";
import { Box, Typography, List, ListItemButton, ListItemText, Divider } from "@mui/material";
import { Plus } from 'lucide-react';
import Cookies from 'js-cookie';
import { useUser } from '../providers/user-provider';
import { useToast } from '../providers/toaster-provider';
import DeletePopup from "./popup/delete";

interface ManageCompanyMenuProps {
    onBack: () => void;
    onCreateCompany: () => void;
}

interface Company {
    id: string;
    name: string;
    [key: string]: any;
}

const ManageCompanyMenu: React.FC<ManageCompanyMenuProps> = ({ onBack, onCreateCompany }) => {
    const { showSuccess } = useToast();
    const { user: currentUser, setUser } = useUser();
    const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
    const [selectedCompanyToSwitch, setSelectedCompanyToSwitch] = useState<Company | null>(null);

    const getCompanies = (): Company[] => {
        try {
            const companiesCookie = Cookies.get('entities');
            return companiesCookie ? JSON.parse(companiesCookie) : [];
        } catch {
            return [];
        }
    };

    const getCurrentCompany = (): Company | null => {
        if (!currentUser?.entityId) return null;
        const companies = getCompanies();
        return companies.find(c => c.entityId === currentUser.entityId) || null;
    };

    const companies = getCompanies();
    const currentCompany = getCurrentCompany();

    const handleCompanySwitch = (company: Company) => {
        setSelectedCompanyToSwitch(company);
        setShowSwitchConfirm(true);
    };

    const confirmCompanySwitch = () => {
        if (selectedCompanyToSwitch) {
            const updatedUser = { ...currentUser, entityId: selectedCompanyToSwitch.entityId };
            setUser(updatedUser);
            Cookies.set('user', JSON.stringify(updatedUser));
            showSuccess('Switched to company: ' + selectedCompanyToSwitch.entityName);
            setShowSwitchConfirm(false);
            setSelectedCompanyToSwitch(null);
            onBack();
            window.location.href = '/transaction';
        }
    };

    return (
        <Box sx={{ minWidth: 280, borderRadius: 2, boxShadow: 3, p: 2, bgcolor: 'background.paper' }}>
            <Box display="flex" justifyContent="center" alignItems="center">
               <Typography fontWeight={600} fontSize={12} color="text.secondary">
                    SWITCH COMPANY
                </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            {companies.filter(company => company.entityId !== currentCompany?.entityId).length > 0 && (
                <Box sx={{ 
                    maxHeight: 200, 
                    overflowY: 'auto', 
                    mb: 2,
                }}>
                    <List disablePadding>
                        {companies.filter(company => company.entityId !== currentCompany?.entityId).map((company) => (
                            <ListItemButton 
                                key={company.entityId}
                                onClick={() => handleCompanySwitch(company)}
                                sx={{ 
                                    height: 36,
                                    borderBottom: '1px solid #f0f0f0',
                                    '&:hover': {
                                        bgcolor: 'rgba(0, 182, 155, 0.1)'
                                    }
                                }}
                            >
                                <ListItemText 
                                    primary={<Typography fontWeight={500} fontSize={14}>{company.entityName}</Typography>} 
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            )}
            <ListItemButton 
                onClick={() => {
                    onBack();
                    onCreateCompany();
                }}
                sx={{ 
                    borderRadius: 1, 
                    height: 40,
                    border: '1px dashed var(--color-primary)',
                    justifyContent: 'center',
                    gap: 1
                }}
            >
                <Plus size={16} style={{ color: 'var(--color-primary)' }} />
                <Typography fontWeight={500} fontSize={14} sx={{ color: 'var(--color-primary)' }}>
                    Add New Company
                </Typography>
            </ListItemButton>
            <DeletePopup
                open={showSwitchConfirm}
                message="Switch Company"
                description={`Are you sure you want to switch to ${selectedCompanyToSwitch?.entityName}?`}
                confirmLabel="Switch"
                cancelLabel="Cancel"
                onConfirm={confirmCompanySwitch}
                onCancel={() => {
                    setShowSwitchConfirm(false);
                    setSelectedCompanyToSwitch(null);
                }}
            />
        </Box>
    );
};

export default ManageCompanyMenu;
